import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { QueryResultDto } from '../../../../../../../typescript-axios';
import { LineChartParameters } from '../config';

interface LineChartVisualizationProps {
    data: QueryResultDto;
    parameters?: LineChartParameters;
}

export function LineChartVisualization({ data, parameters }: LineChartVisualizationProps) {
    const xAxis = typeof parameters?.x_axis === 'string' ? parameters.x_axis : null;
    const yAxes = Array.isArray(parameters?.y_axes) ? (parameters.y_axes as string[]) : [];
    const showLegend = parameters?.show_legend === true;
    const groupSeriesBy = typeof parameters?.group_series_by === 'string' ? parameters.group_series_by : null;

    if (!xAxis) return <div className="flex align-items-center justify-content-center h-full text-500">Missing x_axis in parameters</div>;
    if (yAxes.length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">Missing y_axes in parameters</div>;
    if ((data?.rows ?? []).length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">No data</div>;

    // ── Build series ──────────────────────────────────────────────────
    let series: { label: string; data: (number | null)[] }[];
    let xLabels: string[];

    if (groupSeriesBy) {
        // Get all unique group values, preserving order of first appearance
        const groupValues = [...new Set(data.rows.map(row => String(row[groupSeriesBy] ?? '')))]

        // Get all unique x values, preserving order of first appearance
        xLabels = [...new Set(data.rows.map(row => String(row[xAxis] ?? '')))];

        // For each group × y_axis combination, build a series
        series = groupValues.flatMap(groupVal =>
            yAxes.map(yCol => {
                // Index rows for this group by their x value
                const rowsByX = new Map(
                    data.rows
                        .filter(row => String(row[groupSeriesBy] ?? '') === groupVal)
                        .map(row => [String(row[xAxis] ?? ''), row])
                );

                return {
                    label: groupValues.length === 1 ? yCol : `${groupVal}${yAxes.length > 1 ? ` – ${yCol}` : ''}`,
                    data: xLabels.map(x => {
                        const row = rowsByX.get(x);
                        if (!row) return null;
                        const val = row[yCol];
                        return val === null || val === undefined ? null : Number(val);
                    }),
                };
            })
        );
    } else {
        xLabels = data.rows.map(row => String(row[xAxis] ?? ''));
        series = yAxes.map(col => ({
            label: col,
            data: data.rows.map(row => {
                const val = row[col];
                return val === null || val === undefined ? null : Number(val);
            }),
        }));
    }

    return (
        <LineChart
            xAxis={[{ scaleType: 'point', data: xLabels }]}
            series={series}
            hideLegend={!showLegend}
            sx={{ width: '100%', height: '100%' }}
        />
    );
}